// ============================================================================
// Authenticated weigh-ins identity bridge
// ============================================================================
// `/weighins` is a login-required surface whose presenter intentionally remains
// in src/webforms without AuthContext coupling. WeighInsWebform already owns
// all six of its route resources through `useRouteResource`, so unlike
// AddFeedAuthenticatedBridge this bridge injects NO data at all: the only thing
// App still hands the form is the signed-in submitter, and this is the narrow
// app-side seam that reproduces it for the released mount.
//
// Do not move this into src/webforms. A public webform module must never be
// able to inspect or choose an authenticated session; the same rule is why
// FuelingAuthenticatedBridge exists next door for /equipment/* and
// /fuel-supply. The outer LoginScreen gate normally makes the fail-closed
// branch below unreachable, and it is kept anyway so a future pre-gate mount
// cannot inject a public or stale identity into a write surface.
//
// SCOPE OF THE VALUE. `SessionSubmitter` declares exactly one member, `name`,
// because that is the only member any of the seven webform surfaces reads —
// WeighInsWebform reads it twice, both as `sessionSubmitter?.name || ''` (the
// LockedSubmitter display and the initial `teamMember` state). App's own
// `sessionSubmitter` object additionally carries `email`, `profileId` and
// `role`; none of them is read here, and widening this bridge to match would
// claim a contract nothing checks. The `name` fallback chain is character-
// identical to App's and to the other two bridges'.
// ============================================================================
import React from 'react';

import {useAuth} from '../../contexts/AuthContext.jsx';
import type {SessionSubmitter} from '../../webforms/webformsRoutes.js';

type WeighInsPresenterProps = {
  sessionSubmitter?: SessionSubmitter;
};

type WeighInsAuthenticatedBridgeProps = {
  children: React.ReactElement<WeighInsPresenterProps>;
};

export default function WeighInsAuthenticatedBridge({children}: WeighInsAuthenticatedBridgeProps) {
  const {authState} = useAuth();

  if (authState === null || authState === false) return null;

  const sessionSubmitter: SessionSubmitter = {
    name: authState.name || authState.user?.email || '',
  };

  return React.cloneElement(children, {sessionSubmitter});
}
